import React, { useEffect, useState } from 'react'
import backgroundImage from '../../images/bgd/coins.png'
import Header from '../Header/Header'
import Footer from '../Footer/Footer'
import { useWalletContext } from '../WalletContext'
import { pageStyle, pageStyleMobile } from '../Main'
import { useStoreActions, useStoreState } from '../../model/hooks'
import { removeLeadingZeros } from '../../model/tokens/helper'
import { useNavigate } from 'react-router'
import PrimaryButton from '../PrimaryButton'
import { StarknetImage } from '../../svgIcons/StarknetImage'
import { HorizontalStakIcon } from '../../svgIcons/HorizontalStakIcon'
import { GAtrack } from '../../services/ga'
import Popup from '../Popup'
import StarknetLogo from '../../images/icons/starknetLogo.png'
import { PoolOverview, loadPoolOverviewData } from '../charts/chartsApi'
import { numFormat } from '../../blockchainUtils/math'
import axios from 'axios'
import BigNumber from 'bignumber.js'
import { claimReward, getAlreadyClaimedAmount } from '../../services/rewards'
import { Network } from '../../services/token.service'
import { Transaction } from '../../atoms'
import { IncentivePoolsTable } from './incentivePoolsTable'
import { IncentiveWinkIcon } from '../../svgIcons/IncentiveWinkIcon'
import { IncentiveProgressionTable } from './IncentiveProgressionTable'
import { ClockIcon } from '../../svgIcons/ClockIcon'
import { DiscordIcon } from '../../svgIcons/DiscordIcon'

function normalizeSTRKToken(value: BigNumber): BigNumber {
    return value.dividedBy(1e18)
}

export const tokenAddresses = {
    USDC: '0x53c91253bc9682c04929ca02ed00b3e423f6710d2ee7e0d5ebb06f3ecf368a8',
    USDT: '0x68f5c6a61780768455de69077e07e89787839bf8166decfbf92b645209c0fb8',
    DAI: '0xda114221cb83fa859dbdb4c44beeaa0bb37c7537ad5ae66fe5e0efd20e6eb3',
    ETH: '0x49d36570d4e46f48e99674bd3fcc84644ddd6b96f7c741b1562b82f9e004dc7',
    WBTC: '0x3fe2b97c1fd336e750087d68b9b867997fd64a2661ff3ca5a7c771641e8e7ac',
    STRK: '0x4718f5a0fc34cc1af16a1cdee98ffb20c31f5cd61d6ab07201858f4287c938d',
    WSTETH: '0x42b8f0484674ca266ac5d08e4ac6a3fe65bd3129795def2dca5c34ecc5f96d2'
}
export function IncentivePage() {
    const { walletAddress: connectedAddress, connect, networkName: network } = useWalletContext()
    const loadTokens = useStoreActions((actions) => actions.tokens.loadTokens)
    const tokens = useStoreState((actions) => actions.tokens.tokens)
    const [eligibleStarkAmount, setEligibleStarkAmount] = useState(undefined as unknown as string)
    const [claimedStartAmount, setClaimedStarkAmount] = useState(undefined as unknown as string)
    const [lifetimeEligibleStarkAmount, setLifetimeEligibleStarkAmount] = useState(undefined as unknown as string)
    const [proof, setProof] = useState<string[]>()

    const [isClaiming, setIsClaiming] = useState(false)
    const [isWrongNetworkPopupActive, setIsWrongNetworkPopupActive] = useState(false)
    const [aprOverview, setAprOverview] = useState({} as Record<string, { apr: number; incentiveApr: number }>)
    const loadTransaction = useStoreActions((actions) => actions.transaction.loadTransaction)
    const [txHash, setTxHash] = useState('')
    const transaction = useStoreState((state) => state.transaction.getTransaction(txHash))

    const navigate = useNavigate()

    useEffect(() => {
        if (connectedAddress) loadEligibleAmount()
    }, [connectedAddress, network])

    useEffect(() => {
        GAtrack('reward_visit', 'reward_visit', 'reward_visit')
        loadOverview()
    }, [])

    useEffect(() => {
        loadTokens({ network: 'mainnet-alpha' })
    }, [])

    async function loadOverview() {
        const ids = [
            '0x503f36ee47238549e73e691a840dca44530ed8a2dc98c9bd5adaaacec759536',
            '0x43b4920a31bfdbaa560a78b62c82435934af2b026305d11d0624d9fba40b5',
            '0x71273c5c5780b4be42d9e6567b1b1a6934f43ab8abaf975c0c3da219fc4d040',
            '0x7c5b7061e9e2e233b6ba9e084c3574c6575495721b18344c251d0234e1a77a3'
        ]
        const calls = [] as Promise<PoolOverview | undefined>[]
        ids.map((id) => {
            calls.push(loadPoolOverviewData(id))
        })
        const response = await Promise.all(calls)
        const overView = {} as Record<string, { apr: number; incentiveApr: number }>
        response.map((item, index) => {
            if (!item) return
            overView[ids[index]] = { apr: item?.apr.apr_cl, incentiveApr: item?.apr.incentive_apr }
        })
        setAprOverview(overView)
    }

    useEffect(() => {
        if (transaction && (transaction.status === 'ACCEPTED_ON_L2' || transaction.status === 'ACCEPTED_ON_L1')) {
            setIsClaiming(false)
            setTxHash('')
            loadEligibleAmount()
        }
        if (transaction && transaction.status === 'REVERTED') {
            setIsClaiming(false)
            setTxHash('')
        }
    }, [transaction])

    async function loadEligibleAmount() {
        if (!connectedAddress) return
        try {
            const address = removeLeadingZeros(connectedAddress)
            const { data } = await axios.get(`https://myswap-cl-charts.s3.amazonaws.com/data/proofs5/${address}.json`)
            const alreadyClaimed = await getAlreadyClaimedAmount(network as Network)
            const lifetimeAmount = BigNumber(data.amount)
            const proof = data.proof
            if (proof === undefined || proof.length === 0) {
                throw 'No proof'
            }
            const remainingAmount = lifetimeAmount.minus(BigNumber(alreadyClaimed))
            setEligibleStarkAmount(remainingAmount.toFixed(0))
            setClaimedStarkAmount(alreadyClaimed)
            setLifetimeEligibleStarkAmount(lifetimeAmount.toFixed(0))
            setProof(proof)
        } catch (e) {
            setEligibleStarkAmount('0')
        }
    }

    function renderCard(element: JSX.Element, className?: string) {
        return <div className={`${className} flex flex-col bg-[#0C1647] border-[1px] border-[#0C3F6D] px-4 py-[16px] rounded-[16px] items-center justify-center`}>{element}</div>
    }

    // function renderAddLiquidityLine(token1Address: string, token2Address: string, noMarginRight: boolean, fee: number, poolKey: string) {
    //     const token1 = getToken('mainnet-alpha', token1Address)
    //     const token2 = getToken('mainnet-alpha', token2Address)
    //     if (!token1 || !token2) return null

    //     const totalApr = aprOverview[poolKey] ? aprOverview[poolKey].apr + aprOverview[poolKey].incentiveApr : 0
    //     const incentiveApr = aprOverview[poolKey] ? aprOverview[poolKey].incentiveApr : 0

    //     return renderCard(
    //         <>
    //             <div className='flex flex-col items-center'>
    //                 <div className='flex items-center'>
    //                     <div>
    //                         <img src={token1.icon} alt='' width={32} height={32} />
    //                     </div>
    //                     <div className='relative left-[-10px]'>
    //                         <img src={token2.icon} alt='' width={32} height={32} />
    //                     </div>
    //                 </div>
    //                 <span className='mt-3 text-white font-[400] text-[24px]'>{`${token1!.symbol}/${token2?.symbol}`}</span>
    //             </div>
    //             <div style={{ color: 'rgba(255, 255, 255, 0.50)' }} className='text-[12px] mt-[20px] flex flex-col items-center'>
    //                 <div className='font-bold bg-[#0C3F6D] flex items-center h-[24px] rounded-[8px] p-2 '>
    //                     <span>Total APR:</span>
    //                     <span className='ml-1 text-[#0AE8F9]'>{`${numFormat(totalApr.toFixed(2))}%`}</span>
    //                 </div>
    //                 <span className='mt-[6px]'>{`(${numFormat(incentiveApr.toFixed(2))}% Incentives)`}</span>
    //             </div>
    //             <PrimaryButton
    //                 className='mt-[22px] w-[146px]'
    //                 onClick={() => {
    //                     if (!network || network !== 'mainnet-alpha') return setIsWrongNetworkPopupActive(true)
    //                     GAtrack(
    //                         `reward_route_add_liq_${token1!.symbol}/${token2?.symbol}`,
    //                         `reward_route_add_liq_${token1!.symbol}/${token2?.symbol}`,
    //                         `reward_route_add_liq_${token1!.symbol}/${token2?.symbol}`
    //                     )
    //                     const ename = 'mint_add_position_from_rewards'
    //                     GAtrack(ename, ename, ename)
    //                     navigate(`/pools?token0=${token1Address}&token1=${token2Address}&default_fee=${fee || 0.05}`)
    //                 }}
    //                 size='sm'
    //                 theme='filled'>
    //                 <span className='text-[18px]'>Add Liquidity</span>
    //             </PrimaryButton>
    //         </>,
    //         `lg:w-[258px] w-full mt-4 lg:mt-0 ${noMarginRight ? '' : 'lg:mr-[48px]'}`
    //     )
    // }

    function renderParticipateSection() {
        return (
            <div className='flex flex-col px-3 lg:px-0'>
                <span className='text-[24px] font-[300] mt-[42px] font-bold mb-[12px] z-[10]'>Add liquidity now and keep your capital efficient to maximize your rewards!</span>
                <IncentivePoolsTable setIsWrongNetworkPopupActive={() => setIsWrongNetworkPopupActive(true)} />
            </div>
        )
    }

    function renderConnectForStark() {
        return (
            <div className='flex items-center '>
                <div className='flex flex-col'>
                    <span className='text-[16px] mb-[6px]'>Connect with your mainnet account to check eligibility for</span>
                    <div className='flex items-center'>
                        <span className='text-[20px] lg:text-[40px] font-[400] mr-[6px]'>STARK</span>
                        <img className='lg:w-[40px] lg:h-[40px] w-[24px] h-[24px]' src={StarknetLogo} />
                    </div>
                </div>
                <PrimaryButton
                    className='flex-shrink-0'
                    onClick={() => {
                        connect()
                    }}
                    size='sm'
                    theme='filled'>
                    <span className='text-[16px]'>Connect wallet</span>
                </PrimaryButton>
            </div>
        )
    }

    function renderClaimStark() {
        const strk = BigNumber(eligibleStarkAmount)
        const strkDec = normalizeSTRKToken(strk)

        return (
            <div className='flex items-center mt-4 lg:mt-0 justify-between w-full lg:flex-row flex-col'>
                <div className='flex flex-col'>
                    {eligibleStarkAmount ? <span className='uppercase text-[16px] mb-1 text-center lg:text-start'>Available to claim</span> : null}
                    <div className='flex items-center'>
                        {eligibleStarkAmount ? <span className='lg:text-[40px] text-[40px] font-[400]'>{`${numFormat(strkDec.toFixed())}`}</span> : null}
                        <HorizontalStakIcon className='ml-[6px] h-[50px] w-[170px]' />
                    </div>
                </div>

                <PrimaryButton
                    className='lg:mt-2 mt-4 w-[146px]'
                    isLoading={isClaiming}
                    isDisabled={!eligibleStarkAmount || eligibleStarkAmount === '0'}
                    onClick={doClaim}
                    size='sm'
                    theme='filled'>
                    <span className='text-[18px]'>Claim Now</span>
                </PrimaryButton>
            </div>
        )
    }

    function renderClaimedStark() {
        const strk = BigNumber(claimedStartAmount)
        const strkDec = normalizeSTRKToken(strk)

        return (
            <div className='flex items-center mt-4 lg:mt-0 justify-between w-full lg:flex-row flex-col'>
                <div className='flex flex-col'>
                    <span className='uppercase text-[16px] text-center lg:text-start mb-1'>TOTAL CLAIMED</span>
                    <div className='flex items-center'>
                        {<span className='lg:text-[40px] text-[40px] font-[400] '>{`${!claimedStartAmount || !strkDec.gt(0) ? 0 : numFormat(strkDec.toFixed())}`}</span>}
                        <HorizontalStakIcon className='h-[50px] w-[170px] ml-[6px]' />
                    </div>
                </div>
            </div>
        )
    }

    function renderConnectedStarkClaim() {
        // const strk = BigNumber(eligibleStarkAmount)
        // if (strk.isEqualTo(0)) {
        //     return renderStarkNoClaim()
        // }
        return renderClaimStark()
    }

    async function doClaim() {
        try {
            if (network === 'mainnet-alpha') GAtrack('claim_clicked', 'claim_clicked', 'claim_clicked')

            setIsClaiming(true)
            const claim_tx = await claimReward(network as Network, lifetimeEligibleStarkAmount, proof!)
            const tx = (claim_tx as { success: boolean; error?: string; tx: Transaction | undefined })!.tx
            if (network === 'mainnet-alpha') GAtrack('tx_initiated_claim', 'tx_initiated_claim', 'claim')
            if (tx) {
                const timestampSent = new Date().getTime()
                loadTransaction({ txPayload: { [tx.hash]: { ...tx, labelFields: { timestampSent: timestampSent } } }, isGoogleLog: network === 'mainnet-alpha' })
                setTxHash(tx.hash)
            } else {
                if (network === 'mainnet-alpha') GAtrack('claim_tx_aborted', 'claim_tx_aborted', 'claim_tx_aborted')
                setIsClaiming(false)
            }
        } catch (e: any) {
            setIsClaiming(false)
        }
    }

    function renderStarkNoClaim() {
        return (
            <>
                <div className='flex flex-col mr-[50px]'>
                    <div className='flex items-center'>
                        <span className='text-[22px] font-[400] mr-[6px]'>{`STARK`}</span>
                        <img className='w-[22px] h-[22px]' src={StarknetLogo} />
                    </div>
                    <span className='text-[16px] leading-[1.3] '>Currently, no STRK is available for you to claim. Add liquidity to start generating STRK rewards!</span>
                </div>
            </>
        )
    }

    function renderClaimCardContent() {
        if (!network || network !== 'mainnet-alpha' || !connectedAddress) {
            return renderConnectForStark()
        }
        return <div className='flex items-center w-full h-full'>{renderConnectedStarkClaim()}</div>
    }

    function renderClaimContent() {
        return (
            <div className='flex flex-col lg:px-0 px-3'>
                <span className='text-[24px] mt-[52px] font-bold'>Claim Your Rewards and Compound Them to Earn Even More!</span>
                <span className='text-[18px] mt-2 mb-[12px]'>
                    Your rewards are calculated based on the total liquidity provider (LP) fees generated from your capital each period.
                </span>
                <div className='flex'>
                    <div className='flex bg-[#0C3F6D] rounded-[4px] p-2 items-start lg:items-center text-[12px] text-[#0AE8F9]'>
                        <IncentiveWinkIcon className='mr-2' />
                        <span className='font-bold mr-1'>Tip:</span>
                        <span>Boost your earnings by reinvesting your accrued STRK into the most profitable pool, and ensure that the pool stays 'in range'.</span>
                    </div>
                </div>
                <div className='flex lg:flex-row flex-col w-full items-center'>
                    {connectedAddress ? renderCard(renderClaimedStark(), ' w-full flex-1 p-8 items-start mt-[24px] lg:mr-4') : null}
                    {renderCard(renderClaimCardContent(), `w-full  flex-1 lg:mr-[40px] p-8 items-start mt-[24px] ${connectedAddress ? '' : 'max-w-[528px]'}`)}
                </div>
            </div>
        )
    }

    function renderContent() {
        return (
            <div className='relative w-full'>
                <div className='relative flex flex-col w-full leading-none p-4 lg:p-0 max-w-[1000px] ' style={{ zIndex: 10 }}>
                    <div className='relative w-full flex items-center lg:text-[46px] text-[46px] font-bold justify-start'>
                        <span className='mr-[14px]'>40M</span>
                        <img className='mr-[14px] lg:w-[60px] lg:h-[60px] w-[50px] h-[50px]' src={StarknetLogo} />
                        <span>{'STRK'}</span>
                    </div>
                    <span className='mt-4 inline  items-start lg:items-center lg:flex items-center'>
                        <StarknetImage className='inline  w-[180px] h-[26px] mr-2 mb-2 lg:mb-0 ' />
                        <span className='inline text-[26px] font-[300] lg:text-[26px]'>DeFi Incentive Spring Incentive Program </span>
                    </span>
                    <span className='leading-[1.4] flex flex-col text-[18px] text-white mt-5 max-w-[780px]'>
                        <span>
                            Earn STRK and high yields by providing liquidity in one or more of the eligible pools listed below. To learn how to maximize your rewards and understand
                            their calculation,
                        </span>
                        <span onClick={() => window.open('https://blog.myswap.xyz/myswap-incentive-program-rules')} className='text-[#0AE8F9] cursor-pointer underline font-bold'>
                            visit our explainer page
                        </span>
                    </span>
                </div>
                {renderParticipateSection()}
                {renderClaimContent()}
                <div
                    className='pointer-events-none lg:flex hidden absolute right-0 top-[-100px] h-[618px] w-[618px]'
                    style={{ backgroundSize: 'contain', backgroundImage: `url(${backgroundImage})` }}
                />

                <IncentiveProgressionTable />

                <div className='flex mt-[32px] items-center justify-between lg:flex-row flex-col'>
                    <div className='flex bg-[#0C3F6D] rounded-[4px] p-2 items-start lg:items-center text-[12px] text-[#0AE8F9]'>
                        <ClockIcon className='mr-2' />
                        <span>Rewards claim deadline of August 31st may be subject to extension as per the Starknet Defi Program update.</span>
                    </div>
                    <span className='ml-2 mt-2 lg:mt-0 inline text-[12px] text-white'>
                        <span className=''>For help and support, please join</span>
                        <span onClick={() => window.open('https://discord.com/invite/gbxgWHzqE4')} className='cursor-pointer mx-1 underline'>
                            our Discord support channel.
                        </span>
                        <DiscordIcon className='inline-flex w-[12px] h-[12px]' />
                    </span>
                </div>
            </div>
        )
    }

    function renderWrongNetworkPopup() {
        if (!isWrongNetworkPopupActive) return null

        return (
            <Popup background='#010620' title='Switch to Mainnet' onClose={() => setIsWrongNetworkPopupActive(false)} open={isWrongNetworkPopupActive}>
                <div className='flex flex-col mt-4'>
                    <span className='text-[18px] font-bold space-x-2'>
                        <span className='whitespace-nowrap overflow-hidden text-ellipsis'>{'Switch to'}</span>
                        <br className='sm:hidden' /> {/* Hide line break on larger screens */}
                        {/* <StarknetLogoIcon className='inline w-[24px] flex-shrink-0 h-[24px] mx-2' /> */}
                        <img className='inline w-[24px] flex-shrink-0 h-[24px] mx-2' src={StarknetLogo} />
                        <span className='inline'>{'Mainnet in your wallet to participate.'}</span>
                    </span>
                    <PrimaryButton onClick={() => setIsWrongNetworkPopupActive(false)} theme='filled' className='bg-[#0AE8F9] mt-4'>
                        <span>Got it</span>
                    </PrimaryButton>
                </div>
            </Popup>
        )
    }

    return (
        <div className='h-full relative pb-[48px] lg:pb-0'>
            <div className='lg:pt-6 w-full max-w-[98%] m-auto'>
                <Header selectedTab={'rewards'} className={''} />
            </div>
            <div className='container mx-auto'>
                <div className='flex w-full mt-[40px] pb-[80px]'>{renderContent()}</div>
            </div>
            <Footer selectedTab={'rewards'} />
            <div style={pageStyle} className={`hidden lg:flex z-0 absolute h-full w-full bg-[#00176A] pointer-events-none`} />
            <div style={pageStyleMobile} className={`lg:hidden z-0 absolute h-full w-full bg-[#00176A] pointer-events-none`} />
            {renderWrongNetworkPopup()}
        </div>
    )
}
